// GlobalSearch — ⌘K modal с fuzzy-search по всему admin'у.
//
// Поведение:
//   • Cmd/Ctrl + K в любом месте — открывает.
//   • Поиск идёт по 4 коллекциям параллельно: articles (title), tags (name),
//     series (title), editors (email + name). Дебаунс 150ms.
//   • Стрелки ↑/↓ — навигация по результатам, Enter — открыть.
//   • Esc — закрыть. Click outside — закрыть.
//   • При empty query показываем "Recent" — 5 свежих articles + быстрые
//     ярлыки на Dashboard/New article/Drafts/Scheduled.

import { useEffect, useMemo, useRef, useState } from "react";
import { list } from "../lib/pb";
import { navigate } from "../App";
import type { Article, Editor, Series, Tag } from "../lib/types";

type Result =
  | { type: "article"; id: string; title: string; status: string }
  | { type: "tag"; id: string; name: string; slug: string }
  | { type: "series"; id: string; title: string; slug: string }
  | { type: "editor"; id: string; name: string; email: string }
  | { type: "action"; id: string; title: string; href: string; hint?: string };

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function GlobalSearch({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const QUICK_ACTIONS: Result[] = useMemo(() => [
    { type: "action", id: "new", title: "+ New article", href: "#/article/new", hint: "Create a draft" },
    { type: "action", id: "dashboard", title: "Dashboard", href: "#/dashboard" },
    { type: "action", id: "drafts", title: "Drafts", href: "#/drafts" },
    { type: "action", id: "scheduled", title: "Scheduled", href: "#/scheduled" },
    { type: "action", id: "media", title: "Media Library", href: "#/media" },
    { type: "action", id: "series", title: "Series", href: "#/series" },
    { type: "action", id: "tags", title: "Tags", href: "#/tags" },
    { type: "action", id: "audit", title: "Audit log", href: "#/audit" },
    { type: "action", id: "settings", title: "Settings", href: "#/settings" },
  ], []);

  // Reset state every time modal opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      // Show actions immediately as default
      setResults(QUICK_ACTIONS);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open, QUICK_ACTIONS]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) {
      setResults(QUICK_ACTIONS);
      setSelected(0);
      return;
    }

    const q = query.trim();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const escaped = q.replace(/'/g, "\\'");
        const [arts, tags, series, eds] = await Promise.all([
          list<Article>("articles", {
            filter: `title ~ '${escaped}'`,
            perPage: 8,
            sort: "-updated",
          }).catch(() => ({ items: [] as Article[] })),
          list<Tag>("tags", {
            filter: `name ~ '${escaped}' || slug ~ '${escaped}'`,
            perPage: 5,
          }).catch(() => ({ items: [] as Tag[] })),
          list<Series>("series", {
            filter: `title ~ '${escaped}'`,
            perPage: 5,
          }).catch(() => ({ items: [] as Series[] })),
          list<Editor>("editors", {
            filter: `name ~ '${escaped}' || email ~ '${escaped}'`,
            perPage: 5,
          }).catch(() => ({ items: [] as Editor[] })),
        ]);
        const combined: Result[] = [
          ...arts.items.map((a) => ({ type: "article" as const, id: a.id, title: a.title, status: a.status })),
          ...series.items.map((s) => ({ type: "series" as const, id: s.id, title: s.title, slug: s.slug })),
          ...tags.items.map((t) => ({ type: "tag" as const, id: t.id, name: t.name, slug: t.slug })),
          ...eds.items.map((e) => ({ type: "editor" as const, id: e.id, name: e.name, email: e.email })),
        ];
        // Inject matching quick actions on top if query matches them
        const matchingActions = QUICK_ACTIONS.filter(
          (a) => a.type === "action" && a.title.toLowerCase().includes(q.toLowerCase()),
        );
        setResults([...matchingActions, ...combined]);
        setSelected(0);
      } finally {
        setLoading(false);
      }
    }, 150);

    return () => clearTimeout(t);
  }, [query, open, QUICK_ACTIONS]);

  function openResult(r: Result) {
    switch (r.type) {
      case "article": navigate(`/article/${r.id}`); break;
      case "tag":     navigate("/tags"); break;
      case "series":  navigate("/series"); break;
      case "editor":  navigate("/settings"); break;
      case "action":
        window.location.hash = r.href.replace(/^#/, "");
        break;
    }
    onClose();
  }

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (results[selected]) openResult(results[selected]);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, selected]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 580,
          maxWidth: "94vw",
          background: "var(--bg-1)",
          border: "1px solid var(--border-1)",
          borderRadius: 10,
          boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        {/* Input */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border-1)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "var(--text-3)", fontSize: 16 }}>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, tags, series, editors…"
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--text-0)",
              fontSize: 16,
              fontFamily: "inherit",
            }}
          />
          {loading && <span style={{ color: "var(--text-3)", fontSize: 12 }}>…</span>}
          <kbd className="kbd">esc</kbd>
        </div>

        {/* Results list */}
        <div style={{ maxHeight: "55vh", overflow: "auto" }}>
          {results.length === 0 ? (
            <div className="empty" style={{ padding: 32 }}>
              No matches.
            </div>
          ) : (
            results.map((r, i) => (
              <ResultRow
                key={`${r.type}-${r.id}`}
                result={r}
                active={i === selected}
                onClick={() => openResult(r)}
                onHover={() => setSelected(i)}
              />
            ))
          )}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: "10px 18px",
          borderTop: "1px solid var(--border-1)",
          fontSize: 11,
          color: "var(--text-3)",
          display: "flex",
          gap: 16,
          justifyContent: "flex-end",
          background: "var(--bg-0)",
        }}>
          <span><kbd className="kbd">↑↓</kbd> navigate</span>
          <span><kbd className="kbd">↵</kbd> open</span>
          <span><kbd className="kbd">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

function ResultRow({
  result,
  active,
  onClick,
  onHover,
}: {
  result: Result;
  active: boolean;
  onClick: () => void;
  onHover: () => void;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "10px 18px",
        background: active ? "rgba(184,134,74,0.12)" : "transparent",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      <TypeIcon type={result.type} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: "var(--text-0)",
          fontSize: 14,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {primaryLabel(result)}
        </div>
        <div style={{ color: "var(--text-3)", fontSize: 11, marginTop: 2 }}>
          {secondaryLabel(result)}
        </div>
      </div>
      {result.type === "article" && (
        <span className={`badge badge-${result.status}`} style={{ fontSize: 10 }}>{result.status}</span>
      )}
    </button>
  );
}

function primaryLabel(r: Result): string {
  switch (r.type) {
    case "article": return r.title;
    case "tag":     return `#${r.name}`;
    case "series":  return r.title;
    case "editor":  return r.name || r.email;
    case "action":  return r.title;
  }
}

function secondaryLabel(r: Result): string {
  switch (r.type) {
    case "article": return "Article";
    case "tag":     return `Tag · /${r.slug}`;
    case "series":  return `Series · /${r.slug}`;
    case "editor":  return `Editor · ${r.email}`;
    case "action":  return r.hint || "Quick action";
  }
}

function TypeIcon({ type }: { type: Result["type"] }) {
  const m: Record<Result["type"], string> = {
    article: "▦",
    tag:     "#",
    series:  "❏",
    editor:  "◉",
    action:  "→",
  };
  return (
    <span style={{
      width: 28, height: 28,
      display: "grid", placeItems: "center",
      background: "var(--bg-2)",
      border: "1px solid var(--border-1)",
      borderRadius: 6,
      color: "var(--text-2)",
      fontSize: 13,
      flexShrink: 0,
    }}>
      {m[type]}
    </span>
  );
}
